import { Box, Button, Typography } from '@mui/material';
import { AccountCategory } from '@/interfaces/balanceSheet';
import AccountsGrid from '@/components/grids/AccountsGrid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocalization } from '@/hooks/useLocalization';

interface DrilldownViewProps {
  category: AccountCategory | null;
  onBack: () => void;
  children: React.ReactNode;
  /**
   * Groups the drilled-down accounts by the second tier of Account Type, with a
   * subtotal per group (EUD-886). Only the balance sheet wants this: its
   * categories are classifications, so the subcategory is the next tier down.
   * Callers that drill into a different grouping — institution, data source, a
   * tag — pass nothing and keep the plain list.
   */
  groupBySubcategory?: boolean;
  /**
   * The accounts grid grows with its rows up to the space left on the page,
   * then scrolls inside. Off by default: a widget keeps its fixed height.
   */
  fillHeight?: boolean;
}

export function DrilldownView({
  category,
  onBack,
  children,
  groupBySubcategory = false,
  fillHeight = false,
}: DrilldownViewProps) {
  const { t } = useLocalization();
  if (!category) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        '& .MuiDataGrid-toolbar': {
          px: { xs: 2, sm: 0 },
        },
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        mb={2}
        px={{ xs: 3, sm: 0 }}
      >
        <Button
          variant='outlined'
          color='primary'
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
        >
          {t('back')}
        </Button>
        <Typography
          variant='h6'
          sx={{ ml: 2 }}
        >
          {category.name}
        </Typography>
      </Box>
      <AccountsGrid
        endDate={new Date().toISOString()}
        isWidget={true}
        filteredAccounts={category.accounts}
        groupBySubcategory={groupBySubcategory}
        containerSize={fillHeight ? 'fill' : undefined}
      />
    </Box>
  );
}
